import { useEffect, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { AudioLines, Loader2, RotateCcw, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Turnstile, turnstileEnabled } from "@/components/Turnstile";

/* Second step after a correct password when two-factor is on: authenticator or backup code. */
export const MfaStep = ({ mfaToken, onDone, onBack }) => {
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { await api.post("/auth/2fa/verify", { mfa_token: mfaToken, code, remember }); await onDone(); }
    catch (err) { setError(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="mt-10 space-y-5" data-testid="mfa-form">
      <div className="flex items-center gap-2 text-emerald-700"><ShieldCheck size={18} /><span className="text-sm font-medium">Two-factor check</span></div>
      <p className="text-sm text-zinc-500">Enter the 6-digit code from your authenticator app, or one of your backup codes.</p>
      <Input inputMode="numeric" autoComplete="one-time-code" autoFocus value={code} onChange={(e) => setCode(e.target.value)} placeholder="123 456" className="h-12 bg-white text-center font-mono text-xl tracking-widest" data-testid="mfa-code-input" />
      <label className="flex items-center gap-2 text-sm text-zinc-600"><input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="mfa-remember" />Don't ask again on this device for 30 days</label>
      {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="mfa-error">{error}</p>}
      <button type="submit" disabled={busy || code.replace(/\s/g, "").length < 6} className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-zinc-900 text-sm font-medium text-white disabled:opacity-60" data-testid="mfa-submit">{busy && <Loader2 size={16} className="animate-spin" />}Verify</button>
      {onBack && <button type="button" onClick={onBack} className="text-sm text-zinc-500 underline-offset-4 hover:underline" data-testid="mfa-back">Back</button>}
    </form>
  );
};


export default function Login() {
  const { user, login, register, refreshUser } = useAuth();
  const [params] = useSearchParams();
  // Invite codes travel in the URL fragment (never sent to the server or logs) and are removed from the address bar once read.
  const [inviteToken] = useState(() => {
    const fromHash = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("invite");
    const t = fromHash || params.get("invite") || "";
    if (t) window.history.replaceState(null, "", "/login");
    return t;
  });
  const [invite, setInvite] = useState(null);
  const [mode, setMode] = useState(inviteToken ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [signupsOpen, setSignupsOpen] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [reactivate, setReactivate] = useState(null);
  const [mfa, setMfa] = useState(null);
  const [pending, setPending] = useState(null);
  const [forgot, setForgot] = useState(false);
  const [tsToken, setTsToken] = useState("");
  const [tsKey, setTsKey] = useState(0);
  const [honey, setHoney] = useState("");
  useEffect(() => { api.get("/auth/signup-status").then((r) => setSignupsOpen(r.data.signups_open)).catch(() => {}); }, []);
  useEffect(() => {
    if (!inviteToken) return;
    api.get(`/auth/invite/${inviteToken}`).then((r) => {
      setInvite({ ok: true, ...r.data });
      setForm((f) => ({ ...f, name: f.name || r.data.note || "", email: f.email || r.data.email || "" }));
    }).catch((e) => setInvite({ ok: false, error: formatApiError(e) }));
  }, [inviteToken]);
  if (user) return <Navigate to="/albums?sort=recent" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setDisabled(false);
    setReactivate(null);
    if (honey) return;
    if (turnstileEnabled() && !tsToken) { setError("Please wait for the bot check to finish."); return; }
    setBusy(true);
    try {
      if (mode === "login") {
        const res = await login(form.email, form.password, tsToken);
        if (res?.mfa_required) setMfa(res.mfa_token);
      } else {
        const res = await register(form.name, form.email, form.password, inviteToken, tsToken);
        if (res?.pending_approval) setPending(res.email);
      }
    } catch (err) {
      setError(formatApiError(err));
      if (mode === "login" && err.response?.status === 403 && /disabled/i.test(err.response?.data?.detail || "")) setDisabled(true);
    } finally {
      setBusy(false);
      setTsToken(""); setTsKey((k) => k + 1);
    }
  };
  const askReactivation = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reactivate-request", { email: form.email, password: form.password });
      setReactivate(data);
    } catch (err) { setError(formatApiError(err)); }
    setBusy(false);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="grid min-h-screen bg-[#FAF9F6] lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-[#2a2a2e] lg:block">
        <img src="/hero-cassettes.webp" alt="Resonance Harbor" className="absolute inset-0 h-full w-full object-cover" data-testid="login-hero-image" />
      </div>
      <div className="flex items-center px-6 py-16 sm:px-16">
        <div className="w-full max-w-sm animate-rise">
          <div className="mb-12 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-zinc-900 text-white"><AudioLines size={18} /></div>
            <span className="font-heading text-2xl font-bold tracking-tight">Resonance Harbor</span>
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tight sm:text-5xl" data-testid="auth-title">
            {mode === "login" ? "Welcome back." : "Start your library."}
          </h1>
          <p className="mt-3 text-sm text-zinc-500">
            {mode === "login" ? "Sign in to your collection." : "Create an account to upload music and stream music from your OneDrive."}
          </p>
          {invite && (invite.ok ? (
            <p className="mt-4 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="auth-invite-banner">
              You've been invited{invite.note ? ` (${invite.note})` : ""} — your account will join as a {invite.role} with {invite.library_access === "none" ? "no shared-library access yet" : invite.library_access === "download" ? "read-only + download access" : "read-only access"} to the owner's library.
            </p>
          ) : (
            <p className="mt-4 rounded-md bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="auth-invite-invalid">{invite.error}</p>
          ))}
          {mfa ? <MfaStep mfaToken={mfa} onDone={refreshUser} onBack={() => setMfa(null)} /> : pending ? (
            <div className="mt-10 rounded-md border border-sky-200 bg-sky-50 px-4 py-4" data-testid="auth-pending-approval">
              <p className="flex items-center gap-2 text-sm font-medium text-sky-900"><ShieldCheck size={16} />Account created — waiting for approval</p>
              <p className="mt-1.5 text-sm text-sky-800">The library owner has to approve <span className="font-mono text-xs">{pending}</span> before you can sign in. You'll be able to sign in as soon as they do.</p>
              <button type="button" onClick={() => { setPending(null); setMode("login"); }} className="mt-3 text-sm text-sky-900 underline-offset-4 hover:underline" data-testid="auth-pending-back">Back to sign in</button>
            </div>
          ) : (
          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input id="name" value={form.name} onChange={set("name")} data-testid="auth-name-input" className="h-11 bg-white" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="auth-email-input" className="h-11 bg-white" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={mode === "register" ? 8 : 1} value={form.password} onChange={set("password")} data-testid="auth-password-input" className="h-11 bg-white" autoComplete={mode === "register" ? "new-password" : "current-password"} />
              {mode === "login" && <button type="button" onClick={() => setForgot(!forgot)} className="text-xs text-zinc-500 underline-offset-4 hover:text-zinc-900 hover:underline" data-testid="auth-forgot-link">Forgot your password?</button>}
              {forgot && mode === "login" && <p className="rounded-md bg-zinc-100 px-3 py-2 text-xs text-zinc-600" data-testid="auth-forgot-help">Ask the library owner — they can hand you a one-time reset link from Administration → Accounts. It works for 24 hours.</p>}
            </div>
            <input type="text" name="website" tabIndex={-1} autoComplete="off" value={honey} onChange={(e) => setHoney(e.target.value)} className="absolute -left-[9999px] h-0 w-0 opacity-0" aria-hidden="true" data-testid="auth-honeypot" />
            <Turnstile action={mode} onToken={setTsToken} resetKey={`${mode}-${tsKey}`} />
            {error && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="auth-error">{error}</p>}
            {disabled && !reactivate && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3" data-testid="auth-disabled-panel">
                <p className="text-sm text-amber-900">An owner deactivated this account. You can ask them to switch it back on.</p>
                <button type="button" onClick={askReactivation} disabled={busy} data-testid="auth-reactivate-button"
                  className="mt-3 flex h-9 items-center gap-2 rounded-full bg-amber-700 px-4 text-xs font-medium text-white hover:bg-amber-800 disabled:opacity-60">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}Ask an owner to reactivate
                </button>
              </div>
            )}
            {reactivate && (
              <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="auth-reactivate-sent">
                {reactivate.already ? "You've already asked — the owners can see your request." : "Request sent. The owners will see it on their Accounts page and can reactivate you."}
              </p>
            )}
            <button type="submit" disabled={busy} data-testid="auth-submit-button"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-zinc-900 text-sm font-medium text-white transition-[background-color,transform] duration-150 hover:bg-zinc-800 active:scale-[0.98] disabled:opacity-60">
              {busy && <Loader2 size={16} className="animate-spin" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>
          )}
          {!mfa && !pending && (!signupsOpen && mode === "login" && !inviteToken ? (
            <p className="mt-6 text-sm text-zinc-500" data-testid="auth-signups-closed">New sign-ups are closed. Ask the owner for an account.</p>
          ) : <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            data-testid="auth-toggle-mode" className="mt-6 text-sm text-zinc-500 underline-offset-4 hover:text-zinc-900 hover:underline">
            {mode === "login" ? "No account? Create one" : "Already have an account? Sign in"}
          </button>)}
          <p className="mt-10 text-xs text-zinc-400"><Link to="/privacy" className="underline-offset-4 hover:text-zinc-700 hover:underline" data-testid="auth-privacy-link">Privacy policy</Link></p>
        </div>
      </div>
    </div>
  );
}
